/**
 * Alert sounds. Plays on every surfaced error (see `notify.error`) so Operations hears a failed action even
 * when they are not looking at the screen.
 *
 * The error alert is the owner's own clip (2026-09-23), trimmed to 3 seconds: `/sound/error-alert.mp3`,
 * precached by the service worker so it still plays offline. The synthesised two-tone stays as the fallback
 * for when the file cannot be decoded — the scan beep stays synthesised because it must be instant.
 */
let ctx: AudioContext | null = null

function audioContext(): AudioContext | null {
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctor) return null
    if (!ctx) ctx = new Ctor()
    if (ctx.state === 'suspended') void ctx.resume()
    return ctx
  } catch {
    return null
  }
}

/** A single short, pleasant beep — used when the camera scanner recognises a barcode. */
export function playScanSound() {
  const audio = audioContext()
  if (!audio) return
  const now = audio.currentTime
  const osc = audio.createOscillator()
  const gain = audio.createGain()
  osc.type = 'sine'
  osc.frequency.value = 1046 // C6 — distinct from the two-tone error alert
  gain.gain.setValueAtTime(0.0001, now)
  gain.gain.exponentialRampToValueAtTime(0.3, now + 0.01)
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.09)
  osc.connect(gain).connect(audio.destination)
  osc.start(now)
  osc.stop(now + 0.12)
}

/* ------------------------------------------------------------ Error alert */

export const ERROR_SOUND_URL = '/sound/error-alert.mp3'

/** The screens that carry their own scan verdict sounds — a second alert on top of them only confuses the scan. */
const SILENT_PATHS = ['/operations/runner-returns', '/operations/return-to-shipper/new']

export const isSilentErrorPath = (pathname: string) => SILENT_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))

/**
 * One alert per burst. A single failed action often fails several queries at once (the list, its counters and
 * the detail all 500 together); the operator must hear one alert, not five on top of each other.
 */
const BURST_MS = 3200
let lastPlayed = 0

let el: HTMLAudioElement | null = null
function element(): HTMLAudioElement | null {
  if (typeof Audio === 'undefined') return null
  if (!el) {
    el = new Audio(ERROR_SOUND_URL)
    el.preload = 'auto'
  }
  return el
}

export interface AlertOptions {
  /** `false` mutes it — the user's own preference (`useUi.errorSound`). */
  enabled?: boolean
  /** The route being shown; the returns screens stay silent. */
  pathname?: string
  /** Ignores the burst window — for the "جرب الصوت" button in settings. */
  force?: boolean
  /** 0..1; a screen that speaks its own verdict plays the alert under it (default: full). */
  volume?: number
}

/**
 * The owner's clip, falling back to the synthesised two-tone when the file will not play (blocked autoplay is
 * not a fallback case — no sound can play at all until the user has interacted with the page).
 */
export function playAlertSound(opts: AlertOptions = {}) {
  if (opts.enabled === false) return
  const path = opts.pathname ?? (typeof window === 'undefined' ? '' : window.location.pathname)
  if (path && isSilentErrorPath(path)) return
  const now = Date.now()
  if (!opts.force && now - lastPlayed < BURST_MS) return
  lastPlayed = now

  const audio = element()
  if (!audio) return void playTwoTone()
  try {
    audio.currentTime = 0
    audio.volume = Math.min(1, Math.max(0, opts.volume ?? 1))
    const played = audio.play()
    if (played && typeof played.catch === 'function') played.catch(() => playTwoTone())
  } catch {
    playTwoTone()
  }
}

/** Test seam: forget the burst window between cases. */
export const resetAlertThrottle = () => (lastPlayed = 0)

function playTwoTone() {
  const audio = audioContext()
  if (!audio) return
  const now = audio.currentTime
  const tones: [freq: number, start: number, dur: number][] = [
    [880, 0, 0.14],
    [660, 0.16, 0.14],
  ]
  for (const [freq, start, dur] of tones) {
    const osc = audio.createOscillator()
    const gain = audio.createGain()
    osc.type = 'sine'
    osc.frequency.value = freq
    gain.gain.setValueAtTime(0.0001, now + start)
    gain.gain.exponentialRampToValueAtTime(0.35, now + start + 0.015)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur)
    osc.connect(gain).connect(audio.destination)
    osc.start(now + start)
    osc.stop(now + start + dur + 0.02)
  }
}
